// Jixxeo Talk realtime engine: WS for messages/presence/typing + 1:1 WebRTC voice/video calls.
const ICE = [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }];

function tone(pattern) {
  let ctx, timer, stopped = false;
  const beep = () => {
    if (stopped) return;
    try {
      ctx = ctx || new (window.AudioContext || window.webkitAudioContext)();
      pattern.forEach(([f, start, dur]) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.frequency.value = f; g.gain.value = 0.08; o.connect(g); g.connect(ctx.destination);
        o.start(ctx.currentTime + start); o.stop(ctx.currentTime + start + dur);
      });
    } catch {}
  };
  beep(); timer = setInterval(beep, 2600);
  return () => { stopped = true; clearInterval(timer); try { ctx && ctx.close(); } catch {} };
}

export class TalkEngine {
  constructor(emit) {
    this.emit = emit; this.ws = null; this.retry = 0; this.closed = false;
    this.call = null; this.pc = null; this.local = null; this.remote = null; this.stopTone = null; this.pending = [];
  }

  connect() {
    this.closed = false;
    const ws = new WebSocket(window.location.origin.replace(/^http/, "ws") + "/api/ws/talk");
    this.ws = ws;
    ws.onopen = () => { this.retry = 0; this.emit("status", "online"); };
    ws.onmessage = (e) => { try { this.onMsg(JSON.parse(e.data)); } catch {} };
    ws.onclose = () => {
      this.emit("status", "offline");
      if (this.closed) return;
      setTimeout(() => !this.closed && this.connect(), Math.min(15000, 1000 * 2 ** this.retry++));
    };
    clearInterval(this.ping); this.ping = setInterval(() => this.send({ t: "ping" }), 20000);
  }

  destroy() { this.closed = true; clearInterval(this.ping); this.cleanup(); this.leaveGroup(true); try { this.ws && this.ws.close(); } catch {} }
  send(m) { if (this.ws && this.ws.readyState === 1) { this.ws.send(JSON.stringify(m)); return true; } return false; }
  typing(chat_id) { this.send({ t: "typing", chat_id }); }

  // ----- group voice calls (mesh, up to 8) -----
  setGroup(patch) { this.group = patch ? { ...(this.group || {}), ...patch } : null; this.emit("group", this.group ? { ...this.group, peers: { ...this.group.peers } } : null); }

  async joinGroup(chat) {
    if (this.call || this.group) { this.emit("error", "You're already on a call"); return; }
    try { this.gLocal = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } }); }
    catch { this.emit("error", "Microphone permission is needed for calls"); return; }
    this.setGroup({ chat_id: chat.id, name: chat.name, peers: {}, muted: false, state: "joining", startedAt: Date.now() });
    this.send({ t: "gcall_join", chat_id: chat.id });
  }

  gPeer(peer, initiator) {
    const id = peer.user_id;
    const pc = new RTCPeerConnection({ iceServers: ICE });
    const entry = { profile: peer, pc, stream: null, state: "connecting", pending: [] };
    this.gLocal.getTracks().forEach((t) => pc.addTrack(t, this.gLocal));
    pc.ontrack = (e) => { entry.stream = e.streams[0]; this.setGroup({}); };
    pc.onicecandidate = (e) => e.candidate && this.send({ t: "gcall_signal", chat_id: this.group.chat_id, to: id, data: { candidate: e.candidate } });
    pc.onconnectionstatechange = () => { entry.state = pc.connectionState; this.setGroup({}); };
    this.group.peers[id] = entry;
    if (initiator) pc.createOffer().then((o) => pc.setLocalDescription(o)).then(() => this.send({ t: "gcall_signal", chat_id: this.group.chat_id, to: id, data: { sdp: pc.localDescription } }));
    this.setGroup({});
    return entry;
  }

  async gSignal(from, data) {
    const e = this.group?.peers[from]; if (!e) return;
    const pc = e.pc;
    try {
      if (data.sdp) {
        await pc.setRemoteDescription(data.sdp);
        for (const c of e.pending.splice(0)) await pc.addIceCandidate(c).catch(() => {});
        if (data.sdp.type === "offer") { const a = await pc.createAnswer(); await pc.setLocalDescription(a); this.send({ t: "gcall_signal", chat_id: this.group.chat_id, to: from, data: { sdp: pc.localDescription } }); }
      } else if (data.candidate) { if (pc.remoteDescription) await pc.addIceCandidate(data.candidate); else e.pending.push(data.candidate); }
    } catch {}
  }

  gMute() { const t = this.gLocal?.getAudioTracks()[0]; if (!t) return; t.enabled = !t.enabled; this.setGroup({ muted: !t.enabled }); }

  leaveGroup(silent) {
    if (!this.group) return;
    if (!silent) this.send({ t: "gcall_leave", chat_id: this.group.chat_id });
    Object.values(this.group.peers).forEach((p) => { try { p.pc.close(); } catch {} });
    this.gLocal?.getTracks().forEach((t) => t.stop()); this.gLocal = null;
    this.setGroup(null);
  }

  setCall(patch) { this.call = patch ? { ...(this.call || {}), ...patch } : null; this.emit("call", this.call); }

  async media(video) {
    this.local = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
      video: video ? { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } } : false,
    });
    this.emit("local", this.local);
    return this.local;
  }

  makePc() {
    const pc = new RTCPeerConnection({ iceServers: ICE });
    this.remote = new MediaStream();
    pc.ontrack = (e) => { e.streams[0] ? (this.remote = e.streams[0]) : this.remote.addTrack(e.track); this.emit("remote", this.remote); };
    pc.onicecandidate = (e) => e.candidate && this.send({ t: "signal", call_id: this.call?.id, data: { candidate: e.candidate } });
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === "connected") this.setCall({ state: "active", connectedAt: this.call?.connectedAt || Date.now() });
      if (s === "failed") this.setCall({ quality: "failed" });
    };
    this.local?.getTracks().forEach((t) => pc.addTrack(t, this.local));
    this.pc = pc;
    return pc;
  }

  async startCall(other, video) {
    if (this.call || this.group) return false;
    try { await this.media(video); } catch { this.emit("error", video ? "Camera/microphone permission is needed for video calls" : "Microphone permission is needed for calls"); return false; }
    this.setCall({ id: null, other, video, dir: "out", state: "calling", muted: false, camOff: false });
    if (!this.send({ t: "call_invite", to: other.user_id, video })) { this.emit("error", "You're offline"); this.cleanup(); return false; }
    return true;
  }

  async accept() {
    if (!this.call || this.call.dir !== "in") return;
    this.stopRing();
    try { await this.media(this.call.video); } catch {
      try { await this.media(false); this.setCall({ video: false }); } catch { this.emit("error", "Microphone permission is needed"); return this.reject(); }
    }
    this.makePc();
    this.setCall({ state: "connecting" });
    this.send({ t: "call_accept", call_id: this.call.id });
  }

  reject() { if (this.call?.id) this.send({ t: "call_reject", call_id: this.call.id }); this.cleanup(); }
  hangup() { if (this.call?.id) this.send({ t: "call_end", call_id: this.call.id }); this.cleanup(); }

  toggleMute() {
    const t = this.local?.getAudioTracks()[0]; if (!t) return;
    t.enabled = !t.enabled; this.setCall({ muted: !t.enabled });
  }

  toggleCam() {
    const t = this.local?.getVideoTracks()[0]; if (!t) return;
    t.enabled = !t.enabled; this.setCall({ camOff: !t.enabled });
    this.send({ t: "call_media", call_id: this.call?.id, data: { camOff: !t.enabled } });
  }

  async flipCam() {
    const cur = this.local?.getVideoTracks()[0]; if (!cur || !this.pc) return;
    const facing = (cur.getSettings().facingMode === "environment") ? "user" : "environment";
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing } });
      const nt = s.getVideoTracks()[0];
      const sender = this.pc.getSenders().find((x) => x.track && x.track.kind === "video");
      await sender?.replaceTrack(nt);
      this.local.removeTrack(cur); cur.stop(); this.local.addTrack(nt);
      this.emit("local", this.local);
    } catch { this.emit("error", "Couldn't switch camera"); }
  }

  stopRing() { if (this.stopTone) { this.stopTone(); this.stopTone = null; } }

  cleanup() {
    this.stopRing();
    try { this.pc && this.pc.close(); } catch {}
    this.local?.getTracks().forEach((t) => t.stop());
    this.pc = null; this.local = null; this.remote = null; this.pending = [];
    this.setCall(null);
  }

  async onSignal(data) {
    const pc = this.pc; if (!pc) return;
    try {
      if (data.sdp) {
        await pc.setRemoteDescription(data.sdp);
        for (const c of this.pending.splice(0)) await pc.addIceCandidate(c).catch(() => {});
        if (data.sdp.type === "offer") {
          const a = await pc.createAnswer(); await pc.setLocalDescription(a);
          this.send({ t: "signal", call_id: this.call.id, data: { sdp: pc.localDescription } });
        }
      } else if (data.candidate) {
        if (pc.remoteDescription) await pc.addIceCandidate(data.candidate); else this.pending.push(data.candidate);
      }
    } catch {}
  }

  async onMsg(m) {
    switch (m.t) {
      case "call_ringing":
        if (this.call?.dir === "out") { this.setCall({ id: m.call_id, state: "ringing", other: m.to, calleeOnline: m.online }); this.stopTone = tone([[440, 0, 0.4], [480, 0, 0.4]]); }
        break;
      case "call_incoming":
        if (this.call) { this.send({ t: "call_reject", call_id: m.call_id }); break; }
        this.setCall({ id: m.call_id, other: m.from, video: m.video, dir: "in", state: "incoming", muted: false, camOff: false });
        this.stopTone = tone([[880, 0, 0.18], [660, 0.22, 0.18], [880, 0.44, 0.18]]);
        if (navigator.vibrate) navigator.vibrate([300, 200, 300]);
        break;
      case "call_accepted": {
        this.stopRing();
        this.setCall({ state: "connecting" });
        const pc = this.makePc();
        const o = await pc.createOffer(); await pc.setLocalDescription(o);
        this.send({ t: "signal", call_id: this.call.id, data: { sdp: pc.localDescription } });
        break;
      }
      case "call_taken": if (this.call?.id === m.call_id) { this.stopRing(); this.setCall(null); } break;
      case "call_end":
        if (this.call && (!this.call.id || this.call.id === m.call_id)) { this.emit("call_ended", m.status); this.cleanup(); }
        break;
      case "call_error":
        this.emit("error", m.error === "busy" ? "Someone is already on another call" : m.error === "full" ? "This group call is full (8 people max)" : "Can't start this call");
        if (this.group) this.leaveGroup(true); else this.cleanup();
        break;
      case "call_media": this.setCall({ remoteCamOff: !!m.data?.camOff }); break;
      case "signal": this.onSignal(m.data || {}); break;
      case "gcall_joined":
        if (this.group?.chat_id === m.chat_id) { this.setGroup({ state: "live", name: m.name }); m.peers.forEach((p) => this.gPeer(p, true)); }
        break;
      case "gcall_peer_joined": if (this.group?.chat_id === m.chat_id && m.peer && !this.group.peers[m.peer.user_id]) this.gPeer(m.peer, false); break;
      case "gcall_peer_left":
        if (this.group?.chat_id === m.chat_id && this.group.peers[m.user_id]) { try { this.group.peers[m.user_id].pc.close(); } catch {} delete this.group.peers[m.user_id]; this.setGroup({}); }
        break;
      case "gcall_signal": if (this.group?.chat_id === m.chat_id) this.gSignal(m.from, m.data || {}); break;
      default: this.emit(m.t, m);
    }
  }
}
